import FJConstant from '../../constants/FJConstant';
import FJTracker from '../../core/FJTracker/FJTracker';
import EffectManage, { EffectIndex } from '../../core/Effect/EffectManage';
import { convertMaskToRGBA } from '../../util/sm2/MaskUtils';
import { generateThumbnail } from '../../util/sm2/VideoEditorUtils';
import FJPoolStore from '../../store/FJPoolStore';
import { convertMaskToColorImageData, decode } from '../../util/sm2/mask';
import FJUtil from '../../util/FJUtil';
import { renderThumbnails } from '../FJVideoThumbnailStore';
import FJVideoRemoveBaseManage from './FJVideoRemoveBaseManage';

const MAX_THUMBNAIL_WIDTH = 1280;
const MAX_THUMBNAIL_HEIGHT = 1280;

class FJVideoRemoveVideoManage extends FJVideoRemoveBaseManage {
    _videoThumbnailCanvas = null; // 视频时间线缩略图画布

    _customObjectPreviewCanvas = null; // 自定义对象已选中预览画布
    _customObjectPreviewCtx = null; // 自定义对象已选中预览画布上下文

    _allFrameCount = 0; // 总帧数
    _fps = 30; // 帧率

    // 轨迹
    _tracker = null;
    _trackletObjectId = 0;

    // effect
    _effects = [];
    // 背景
    _canvasBackground = null;
    _glBackground = null;
    // 高亮
    _canvasHighlights = null;
    _glObjects = null;
    _thumbnailWorker = null;

    constructor() {
        super();
        this._tracker = new FJTracker();
        this._effects = [EffectManage.Overlay];
    }

    /**
     * @desc 绘制画面
     */
    async draw() {
        if (this._loadStatus !== FJConstant.SUCCESS || !this._source || !this._ctx) return;
        super.draw();
        await this._drawTrackletMasks(this._canvas, this._ctx);
    }

    async _drawTrackletMasks(canvas, ctx, setColors) {
        const currentTime = this._source.currentTime;
        const allTracklets = this.getTracklets();

        const masks = [];
        const colors = [];
        const tracklets = [];
        allTracklets.forEach(tracklet => {
            if (!FJUtil.compareTimes(currentTime, tracklet.currentTime)) return;

            tracklet.masks.map(mask => {
                if (mask != null) {
                    masks.push(mask);
                    tracklets.push(tracklet);
                    colors.push(tracklet.color);
                }
            });
        });

        if (masks.length === 0) return;

        const effectMaskPromises = masks.map(async ({ data, bounds }) => {
            return {
                bounds,
                bitmap: data,
            };
        });

        const effectMasks = await Promise.all(effectMaskPromises);

        const effectParams = {
            frame: canvas,
            masks: effectMasks,
            maskColors: setColors ? setColors : colors,
            totalFrames: this._allFrameCount,
            fps: this._fps,
            width: this._sourceInfo.width,
            height: this._sourceInfo.height,
            actionPoint: null,
        };

        for (let i = 0; i < this._effects.length; i++) {
            const effect = this._effects[i];
            effect.apply(ctx, effectParams, tracklets);
        }
    }

    setSourceUrl(sourceUrl) {
        this._sourceUrl = sourceUrl;
    }

    /**
     * @desc 设置视频源

     * @param {Object} source
     */
    async setSource(source, sourceUrl) {
        try {
            const video = document.createElement('video');
            video.crossOrigin = 'anonymous';
            this._loadStatus = FJConstant.LOADING;
            const sourceInfo = await new Promise((resolve, reject) => {
                video.onloadedmetadata = () => {
                    const fileData = {
                        width: video.videoWidth,
                        height: video.videoHeight,
                    };

                    this._duration = video.duration;

                    this._loadStatus = FJConstant.SUCCESS;
                    resolve(fileData);
                };
                video.onerror = () => {
                    this._loadStatus = FJConstant.ERROR;
                    reject();
                };

                const videoUrl = sourceUrl || URL.createObjectURL(source);
                video.src = videoUrl;
            });

            if (sourceUrl && !source) {
                // 如果是URL源,需要下载文件
                this._sourceUrl = sourceUrl;
                let videoBlob = await FJUtil.getBlobByUrl(sourceUrl);
                if (!videoBlob) {
                    videoBlob = await FJUtil.getBlobByUrl(sourceUrl + '?v=' + Date.now());
                }

                if (!videoBlob) {
                    throw new Error('Failed to load video');
                }
                this._sourceFile = new File([videoBlob], 'video.mp4', { type: 'video/mp4' });
            } else {
                this._sourceFile = source;
            }

            this._source = video;
            this._sourceInfo = sourceInfo;

            this._tracker.createTracklet();
            this._initializeWebGLContext(this._sourceInfo.width, this._sourceInfo.height);
            for (const [i, effect] of this._effects.entries()) {
                const offCanvas = i === EffectIndex.BACKGROUND ? this._canvasBackground : this._canvasHighlights;
                const webglContext = i === EffectIndex.BACKGROUND ? this._glBackground : this._glObjects;

                await effect.setup({
                    width: this._sourceInfo.width,
                    height: this._sourceInfo.height,
                    canvas: offCanvas,
                    gl: webglContext,
                });
            }
        } catch (error) {
            this._loadStatus = FJConstant.ERROR;
            console.error(error);
        }
    }

    /**
     * @desc 更新视频缩略图
     */
    async updateVideoThumbnail() {
        if (this._videoThumbnailCanvas && this._sourceInfo) {
            const thumbnailCanvasSize = this._videoThumbnailCanvas.getBoundingClientRect();
            if (thumbnailCanvasSize.width === 0 || thumbnailCanvasSize.height === 0) {
                return;
            }

            if (this._thumbnailAbortController) {
                this._thumbnailAbortController.abort();
            }

            this._videoThumbnailCanvas.width = thumbnailCanvasSize.width;
            this._videoThumbnailCanvas.height = thumbnailCanvasSize.height;
            const thumbnailSize = {
                width: Math.floor(thumbnailCanvasSize.height * (this._sourceInfo.width / this._sourceInfo.height)),
                height: thumbnailCanvasSize.height,
            };

            await renderThumbnails(this._source.src, this._videoThumbnailCanvas, {
                thumbnailSize,
                renderInfo: null,
                fileInfo: { width: this._sourceInfo.width, height: this._sourceInfo.height },
                start: 0,
                end: this._source.duration,
                setAbortController: abortController => {
                    this._thumbnailAbortController = abortController;
                },
            });

            // let worker = this._thumbnailWorker;
            // if (!worker) {
            //     worker = new Worker(FJConfig.videoThumbnailWorkerPath);
            //     const readyPromise = new Promise((resolve, reject) => {
            //         worker.onmessage = e => {
            //             const msg = e.data;

            //             if (msg.type === 'ready') {
            //                 resolve();
            //             } else if (msg.type === 'error') {
            //                 console.error(msg.message);
            //                 reject(new Error(msg.message));
            //             } else if (msg.type === 'done') {
            //                 const ctx = this._videoThumbnailCanvas.getContext('2d');
            //                 ctx.drawImage(msg.imageBitMap, 0, 0);
            //                 worker.terminate();
            //                 this._thumbnailWorker = null;
            //             } else {
            //                 reject(new Error('Not Ready'));
            //             }
            //         };
            //     });

            //     await readyPromise;
            //     this._thumbnailWorker = worker;
            // }
            // const wasm = await new Promise(resolve => FJWASMLoader.loadWasmBinary(resolve));

            // const offscreenCanvas = new OffscreenCanvas(thumbnailCanvasSize.width, thumbnailCanvasSize.height);

            // const thumbnailSize = {
            //     width: Math.floor(thumbnailCanvasSize.height * (this._sourceInfo.width / this._sourceInfo.height)),
            //     height: thumbnailCanvasSize.height,
            // };
            // this._thumbnailWorker.postMessage(
            //     {
            //         wasm,
            //         offscreenCanvas,
            //         thumbnailSize,
            //         file: this._sourceFile,
            //         fileInfo: { width: this._sourceInfo.width, height: this._sourceInfo.height },
            //         start: 0,
            //         end: this._source.duration,
            //         type: 'RENDER_TUNMBNAILS',
            //         wasmResourceVersion: FJConfig.codecVersion,
            //     },
            //     [offscreenCanvas],
            // );

            // try {
            //     await new Promise((resolve, reject) => {
            //         worker.onmessage = e => {
            //             const msg = e.data;
            //             if (msg.type === 'done') {
            //                 const ctx = this._videoThumbnailCanvas.getContext('2d');
            //                 ctx.drawImage(msg.imageBitMap, 0, 0);

            //                 worker.terminate();
            //                 this._thumbnailWorker = null;
            //                 msg.imageBitMap.close();
            //                 resolve();
            //             } else if (msg.type === 'error') {
            //                 reject(new Error(msg.message));
            //             }
            //         };
            //     });
            // } catch (error) {}
        }
    }

    clearAll() {
        super.clearAll();
        this._tracker.clearTracklets();
    }

    /**
     * @desc 设置视频缩略图画布
     * @param {HTMLCanvasElement} canvas
     */
    setVideoThumbnailCanvas(canvas) {
        this._videoThumbnailCanvas = canvas;
    }

    /**
     * @desc 设置自定义对象已选中预览画布
     * @param {HTMLCanvasElement} canvas
     */
    setCustomObjectPreviewCanvas(canvas) {
        this._customObjectPreviewCanvas = canvas;
        this._customObjectPreviewCtx = canvas.getContext('2d');
    }

    /**
     * @desc 更新自定义对象已选中预览画布
     */
    async updateCustomObjectPreviewCanvas() {
        if (!this._customObjectPreviewCanvas || !this._customObjectPreviewCtx) return;
        const tracklets = this.getTracklets();
        const track = tracklets[this._trackletObjectId];
        if (!track) return;
        const currentTime = this._source.currentTime;

        const mask = track.masks[0];
        if (!mask) {
            this._customObjectPreviewCanvas.width = 0;
            this._customObjectPreviewCanvas.height = 0;
            return;
        }
        const decodedMask = decode([mask.data]);

        const { ctx } = await this._compressMaskForCanvas(decodedMask);
        const isSameTime = FJUtil.compareTimes(currentTime, track.currentTime);
        if (!isSameTime) {
            await this.seek(track.currentTime, true);
        }
        const frame = this._source;
        const canvasFrame = FJPoolStore.getCanvas('2d');
        canvasFrame.width = this._sourceInfo.width;
        canvasFrame.height = this._sourceInfo.height;
        const ctxFrame = canvasFrame.getContext('2d');
        ctxFrame.drawImage(frame, 0, 0, canvasFrame.width, canvasFrame.height);

        const thumbnailCanvas = await generateThumbnail(track, mask, canvasFrame, ctx);
        this._customObjectPreviewCanvas.width = thumbnailCanvas.width;
        this._customObjectPreviewCanvas.height = thumbnailCanvas.height;
        this._customObjectPreviewCtx.drawImage(thumbnailCanvas, 0, 0);

        FJPoolStore.releaseCanvas(canvasFrame);
        // 还原到当前时间
        if (!isSameTime) {
            await this.seek(currentTime);
        }
    }

    /**
     * @desc 获取帧率
     * @returns {Number}
     */
    getFps() {
        return this._fps;
    }

    getCurrentFrame() {
        return Math.floor(this._source.currentTime * this._fps);
    }

    // #region 预测视频相关

    /**
     * @desc 更新轨迹
     * @param {} point
     */
    async updatePoints(points, callback, failCallBack) {
        const canvas = FJPoolStore.getCanvas('2d');
        canvas.width = this._sourceInfo.width;
        canvas.height = this._sourceInfo.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(this._source, 0, 0, canvas.width, canvas.height);
        const base64String = canvas.toDataURL('image/png');

        FJPoolStore.releaseCanvas(canvas);
        await this._tracker.updatePoints(
            {
                currentTime: this._source.currentTime,
                objectId: this._trackletObjectId,
                points,
                base64String,
            },
            callback,
            failCallBack,
        );
    }

    /**
     * @desc 获取视频缩略图Blob
     * @returns {Blob}
     */
    async getThumbnailBlob() {
        const canvas = FJPoolStore.getCanvas('2d');
        const ctx = canvas.getContext('2d');

        const aspectRatio = this._sourceInfo.width / this._sourceInfo.height;

        if (aspectRatio >= 1 && this._sourceInfo.width > MAX_THUMBNAIL_WIDTH) {
            canvas.width = MAX_THUMBNAIL_WIDTH;
            canvas.height = MAX_THUMBNAIL_WIDTH / aspectRatio;
        } else if (aspectRatio < 1 && this._sourceInfo.height > MAX_THUMBNAIL_HEIGHT) {
            canvas.width = MAX_THUMBNAIL_HEIGHT * aspectRatio;
            canvas.height = MAX_THUMBNAIL_HEIGHT;
        } else {
            canvas.width = this._sourceInfo.width;
            canvas.height = this._sourceInfo.height;
        }

        const tracklets = this.getTracklets();
        const tracklet = tracklets[0];

        await this.seek(tracklet.currentTime);

        const maskBlob = await this.getMaskBlob({ r: 255, g: 255, b: 255, a: 255 }, { r: 0, g: 0, b: 0, a: 0 });
        const maskBitmap = await createImageBitmap(maskBlob);

        ctx.drawImage(maskBitmap, 0, 0, canvas.width, canvas.height);

        ctx.save();
        ctx.globalCompositeOperation = 'source-in';
        ctx.drawImage(this._source, 0, 0, canvas.width, canvas.height);
        ctx.restore();

        // 将canvas转换为blob对象
        const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
        FJPoolStore.releaseCanvas(canvas);
        return blob;
    }

    /**
     * @desc 获取原始缩略图URL
     * @returns {String}
     */
    async getOriginalThumbnailUrl() {
        const canvas = FJPoolStore.getCanvas('2d');
        const ctx = canvas.getContext('2d');

        canvas.width = this._sourceInfo.width;
        canvas.height = this._sourceInfo.height;

        const tracklets = this.getTracklets();
        const tracklet = tracklets[0];
        await this.seek(tracklet.currentTime);

        ctx.drawImage(this._source, 0, 0, canvas.width, canvas.height);
        // 将canvas转换为blob对象
        const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
        FJPoolStore.releaseCanvas(canvas);
        return URL.createObjectURL(blob);
    }

    async getMaskBlob(color = { r: 255, g: 255, b: 255, a: 255 }, color2 = { r: 0, g: 0, b: 0, a: 255 }) {
        const canvas = FJPoolStore.getCanvas('2d');
        const ctx = canvas.getContext('2d');
        canvas.width = this._canvas.width;
        canvas.height = this._canvas.height;

        const allTracklets = this.getTracklets();
        const masks = [];
        const colors = [];
        const tracklets = [];

        allTracklets.forEach(tracklet => {
            tracklet.masks.map(mask => {
                if (mask != null) {
                    masks.push(mask);
                    tracklets.push(tracklet);
                    colors.push(tracklet.color);
                }
            });
        });

        const effectMaskPromises = masks.map(async ({ data, bounds }) => {
            return {
                bounds,
                bitmap: data,
            };
        });

        const effectMasks = await Promise.all(effectMaskPromises);

        const mask = effectMasks[0];
        const decodedMask = decode([mask.bitmap]);
        const maskData = decodedMask.data;
        const imageData = convertMaskToColorImageData(maskData, canvas.width, canvas.height, color, color2);
        ctx.putImageData(imageData, 0, 0);

        const blob = await new Promise(resolve => canvas.toBlob(resolve));
        return blob;
    }

    /**
     * @desc 删除所有点
     */
    deleteAllPoints() {
        this._tracker.deleteAllPoints(this._trackletObjectId);
    }

    /**
     * @desc 获取轨迹
     * @returns {Array}
     */
    getTracklets() {
        return this._tracker.getTracklets();
    }

    /**
     * @desc 设置轨迹对象ID
     * @param {Number} objectId
     */
    setTrackletObjectId(objectId) {
        this._trackletObjectId = objectId;
    }
    // #endregion

    _initializeWebGLContext(width, height) {
        // Given that we use highlight and background effects as layers,
        // we need to create two WebGL contexts, one for each set.
        // To avoid memory leaks and too many active contexts,
        // these contexts must be re-used over the lifecycle of the session.

        if (this._canvasHighlights == null && this._glObjects == null) {
            this._canvasHighlights = new OffscreenCanvas(width, height);
            this._glObjects = this._canvasHighlights.getContext('webgl2');

            this._canvasHighlights.addEventListener(
                'webglcontextlost',
                event => {
                    event.preventDefault();
                },
                false,
            );
        } else if (
            this._canvasHighlights != null &&
            (this._canvasHighlights.width !== width || this._canvasHighlights.height !== height)
        ) {
            // Resize canvas and webgl viewport
            this._canvasHighlights.width = width;
            this._canvasHighlights.height = height;
            if (this._glObjects != null) {
                this._glObjects.viewport(0, 0, width, height);
            }
        }

        if (this._canvasBackground == null && this._glBackground == null) {
            this._canvasBackground = new OffscreenCanvas(width, height);
            this._glBackground = this._canvasBackground.getContext('webgl2');

            this._canvasBackground.addEventListener(
                'webglcontextlost',
                event => {
                    event.preventDefault();
                },
                false,
            );
        } else if (
            this._canvasBackground != null &&
            (this._canvasBackground.width != width || this._canvasBackground.height != height)
        ) {
            // Resize canvas and webgl viewport
            this._canvasBackground.width = width;
            this._canvasBackground.height = height;
            if (this._glBackground != null) {
                this._glBackground.viewport(0, 0, width, height);
            }
        }
    }

    /**
     * @desc 设置总帧数
     * @param {Number} allFrameCount
     */
    setAllFrameCount(allDuration) {
        // 计算总帧数并设置
        this._allFrameCount = Math.floor(allDuration * this._fps);
    }

    async startTrack(videoPath, maskPath, websocketCallback) {
        return await this._tracker.startTrack(videoPath, maskPath, this._source.duration, websocketCallback);
    }

    getIsPlaying() {
        return this._isPlaying;
    }

    async _compressMaskForCanvas(decodedMask) {
        const data = convertMaskToRGBA(decodedMask.data);

        const maskCanvas = FJPoolStore.getCanvas('2d', true);
        const maskCtx = maskCanvas.getContext('2d');

        maskCanvas.width = decodedMask.shape[0];
        maskCanvas.height = decodedMask.shape[1];

        const imageData = new ImageData(data, decodedMask.shape[0], decodedMask.shape[1]);
        maskCtx.putImageData(imageData, 0, 0);

        const canvas = new OffscreenCanvas(decodedMask.shape[1], decodedMask.shape[0]);

        const ctx = canvas.getContext('2d');
        ctx.save();
        ctx.rotate(Math.PI / 2);
        // Since the image was previously rotated 90° clockwise, after the image is rotated,
        // we scale the canvas's width using scaleY and height using scaleX.
        ctx.scale(1, -1);
        ctx.drawImage(maskCanvas, 0, 0);
        ctx.restore();

        const compressedData = await canvas.convertToBlob({ type: 'image/png' });

        return { compressedData, ctx };
    }

    /**
     * @desc 重新加载视频源
     */
    async reloadSource() {
        await this.setSource(this._sourceFile, this._sourceUrl);
    }
}

const FJVideoRemoveVideoManageSingleton = new FJVideoRemoveVideoManage();
export default FJVideoRemoveVideoManageSingleton;
export { FJVideoRemoveVideoManage };
